import sdk from '@/sdk';
import * as store from '@/store';
import {rteWebLog} from '@/buried';
import eventBus, {FLOW_JS_LOADED} from '@/eventBus';
import {ServerConfig} from '@/config/routers';
import {getMorphoService, serviceNames} from '@/rte/editor';
import {flowObj} from './flowObj';

const loadFlowJS = (url: string, tryCount = 0, maxTry = 2): Promise<void> => {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;

        script.onload = () => resolve();

        script.onerror = async (error) => {
            document.body.removeChild(script);

            if (tryCount < maxTry) {
                try {
                    const result = await loadFlowJS(url, tryCount + 1, maxTry);
                    resolve(result);
                } catch (e) {
                    reject(e);
                }
            } else {
                reject(new Error(`loadFlowJS failed after ${maxTry} attempts: ${error}`));
            }
        };

        try {
            document.body.appendChild(script);
        } catch (e) {
            reject(e);
        }
    });
};

/**
 * 初始化待办星链
 */
const initTodo = () => {
    const flow = flowObj.getFlowApi();

    if (!flow?.todo) {
        return;
    }

    flow.todo.init?.({
        imid: sdk.as.user?.rlId || '',
    });
};

enum linkRecognizeMessageType {
    CARD_DOM_UPDATE = 'CARD_DOM_UPDATE',
    DATA_LOAD = 'DATA_LOAD'
}

interface CardDomUpdate {
    dom: HTMLElement;
    url: string;
    data: {
        height: number;
    };
}

/**
 * 初始化统一链接识别
 */
const initLinkRecognize = () => {
    // 初始化完成
    eventBus.emit(FLOW_JS_LOADED);

    const proxyBaseUrl = `${ServerConfig.rte}/wiki/ku/third/todo/proxy`;
    const flow = flowObj.getFlowApi();
    const {accessToken} = store.getData(store.KEYS.CUR_DOCUMENT_TOKEN) || {};

    flow?.init({
        proxyBaseUrl,
        requestHeader: {
            'Authorization': accessToken,
        },
    });

    // 监听事件 数据变更
    flow?.linkRecognize?.addListener?.((message: {
        type: linkRecognizeMessageType;
        data: CardDomUpdate;
    }) => {
        const {type, data} = message;

        if (type !== linkRecognizeMessageType.CARD_DOM_UPDATE) {
            return;
        }

        const mentionService = getMorphoService(serviceNames.IMentionService);
        mentionService?.cardHeightChange?.(data.url, data.dom, data.data.height);
    });
};

// 加载 flow.js，并初始化相关业务
export const initFlowJS = async () => {
    if (window.isTocPage) {
        return;
    }
    try {
        if (flowObj.getFlowApi()) {
            return;
        }

        // 加载 flow 资源
        await loadFlowJS(flowObj.url);
        flowObj.setLoadStatus('success');

        // 初始化待办星链 & 统一链接识别
        initTodo();
        initLinkRecognize();
    } catch (error) {
        flowObj.setLoadStatus('fail');

        // 上报错误日志
        rteWebLog({
            id: '80001.8.107',
            data: {
                error,
                message: error instanceof Error
                    ? JSON.stringify(error, ['message', 'arguments', 'type', 'name'])
                    : String(error),
            },
        });
    }
};
